import React, { useState } from 'react';
import { getWidget } from 'shared/widgets';
import styled from 'styled-components';
import { Label } from '@uhg-harmony/uitk-react';
import { StyledPanelTitle } from 'shared/widgets/Text/TextWidgetControl';
import { Features } from 'shared/state/ducks/features';
import Draggable from 'react-draggable';
import Tooltip from 'shared/components/Tooltip';
import Scrollbars from 'react-custom-scrollbars';
import { useDispatch } from 'react-redux';
import {
  syncRepeaterConfigChanges,
  syncRepeaterLayout,
} from 'shared/widgets/RepeaterWidget/RepeaterUtils';
// Use explicit shared imports
import { Icon as SharedIcon } from 'shared/theme/link-ui-react/Icon';
import { Link } from 'shared/theme/link-ui-react/Link';

const SidebarNav = styled.nav<{ dragging: boolean }>`
  width: 365px;
  height: auto;
  display: flex;
  justify-content: center;
  position: fixed;
  top: 60px;
  z-index: 10;
  background: white;
  box-shadow: 0 3px 6px -4px #0000001f, 0 6px 16px #00000014,
    0 9px 28px 8px #0000000d;
  transition: ${({ dragging }) => (dragging ? 'none' : '850ms')};
  will-change: transform;
`;

const NavIcon = styled(Link)`
  font-size: 2rem;
  height: 40px;
  display: flex;
  align-items: center;
  background: #cccac9;
  border-bottom: 1px solid #888888;
  width: 100%;
  cursor: move;
  padding: 0 0.5rem;

  > span {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &:hover {
    fill: #c25608;
  }
`;

const SidebarWrap = styled.div`
  width: 100%;
`;

const StyledWidgetType = styled.span`
  font-family: OptumSans, sans-serif;
  font-size: 16px;
  color: #0c55b8;
  border-left: 5px;
`;
const StyledLabel = styled(Label)`
  margin-left: 0 !important;
`;
export interface WidgetControlWrapperNewProps<T> {
  widgetState: Widget<T>;
  onUpdateConfig: (config: any, defaultWidget: boolean) => void;
  onUpdateDefaultConfig: (config: any, defaultWidget: boolean) => void;
  pages: Array<Page>;
  site: Site;
  updateSite: (site: Site) => void;
  saveSite: () => void;
  openPopup: (open: boolean) => void;
  addChild: (
    type: string,
    initialConfig: T,
    location: number,
    widgetId: string,
    newParentWidgetId: string,
    tabIndex: number
  ) => void;
  currentPage: PageVersion;
  updateCurrentPage: (pageVersion: PageVersion) => void;
  removeWidget: (widgetId: string) => void;
  addTodoState?: (config: any) => void;
  undoState?: any;
  setResetToFalse?: () => void;
  updatePageContent?: (page: any) => void;
  updateWidgetPosition?: (
    bannerWidgetId: string,
    height: number,
    oldHeight: number,
    newPlacement?: string
  ) => void;
  eventList?: Array<EventData>;
  fetchEventList?: (widgetId: string) => Promise<Array<EventData>>;
  saveEvent?: (
    event: EventPayload,
    siteId: number,
    pageId: number
  ) => Promise<EventData>;
  removeEvent?: (eventId: number) => Promise<void>;
  updateEvent?: (
    eventId: number,
    eventPayload: EventPayload
  ) => Promise<EventData>;
  isEditingTemplate?: boolean;
  features?: Features;
  fetchFeatures?: (featureName: string) => Promise<Features>;
}

const WidgetControlWrapperNew: React.FC<WidgetControlWrapperNewProps<any>> = (
  props: WidgetControlWrapperNewProps<any>
) => {
  const {
    onUpdateConfig,
    pages,
    widgetState,
    updateSite,
    saveSite,
    site,
    openPopup,
    currentPage,
    updateCurrentPage,
    addChild,
    removeWidget,
    setResetToFalse,
    isEditingTemplate,
    features,
    fetchFeatures,
  } = props;

  const [sidebar, setSidebar] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 0 });

  const dispatch = useDispatch();

  const WidgetControl = widgetState
    ? getWidget(widgetState.type, true).components.control
    : null;

  const handleUpdateConfig = (config: any) => {
    const oldConfig = widgetState.config;
    const configChanges: Record<string, any> = {};

    Object.keys(config).forEach(key => {
      if (config[key] !== oldConfig[key]) {
        configChanges[key] = config[key];
      }
    });

    const repeaterWidgetId = widgetState.config?.repeaterWidgetId;
    const repeaterSectionId = widgetState.config?.repeaterWidgetIndex;

    let batchHandled = false;
    if (repeaterWidgetId || repeaterSectionId) {
      if (
        configChanges.xLocation ||
        configChanges.yLocation ||
        configChanges.width ||
        configChanges.height
      ) {
        batchHandled = Boolean(
          syncRepeaterLayout({
            changedWidget: { ...widgetState, config },
            currentPage: currentPage,
            oldCurrentPage: currentPage,
            site: props.site,
            pageId: currentPage.pageId,
            dispatch,
          })
        );
      }

      if (
        !batchHandled &&
        Object.keys(configChanges).some(
          key => !['xLocation', 'yLocation', 'width', 'height'].includes(key)
        )
      ) {
        batchHandled = syncRepeaterConfigChanges({
          changedWidget: { ...widgetState, config },
          currentPage: currentPage,
          site: props.site,
          pageId: currentPage.pageId,
          dispatch,
          configChanges,
        });
      }
    }

    // Only update individually if not handled by batch
    if (!batchHandled) {
      onUpdateConfig(config, widgetState.defaultWidget);
    }
    setResetToFalse?.();
  };

  const showSidebar = () => {
    openPopup(!sidebar);
    setSidebar(!sidebar);
  };

  const resetPosition = () => {
    setPosition({ x: 0, y: 0 });
  };

  return widgetState && WidgetControl ? (
    <Draggable
      handle=".drag-handle"
      bounds="body"
      position={position}
      onStart={() => setDragging(true)}
      onStop={(_e, data) => {
        setDragging(false);
        setPosition({ x: data.x, y: data.y });
      }}
    >
      <SidebarNav
        dragging={dragging}
        style={{ right: sidebar ? '5px' : '-100%' }}
      >
        <SidebarWrap>
          <NavIcon className="drag-handle">
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <StyledLabel
                style={StyledPanelTitle}
                data-test-id="configuration-panel-heading"
              >
                Configuration for:{' '}
                <StyledWidgetType>{widgetState?.type}</StyledWidgetType>
              </StyledLabel>
              <div
                style={{ display: 'flex', gap: '0.5rem', marginRight: '8px' }}
              >
                <Tooltip
                  arrow
                  textAlign="left"
                  placement="bottom-start"
                  title={'Revert to default position'}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <SharedIcon
                      icon="Refresh"
                      color="#000000"
                      width={'1.4em'}
                      height={'1.1em'}
                      onClick={resetPosition}
                      title="Reset Position"
                      style={{ cursor: 'pointer' }}
                    />
                  </span>
                </Tooltip>
                <SharedIcon
                  icon="Close"
                  color="#000000"
                  width={'1.6em'}
                  height={'1.1em'}
                  onClick={showSidebar}
                  style={{ cursor: 'pointer' }}
                  data-test-id="close-icon"
                />
              </div>
            </span>
          </NavIcon>
          <Scrollbars autoHeight autoHeightMax={800} autoHide universal>
            <WidgetControl
              onChange={handleUpdateConfig}
              pages={pages}
              value={widgetState?.config}
              widgetState={widgetState}
              site={site}
              updateSite={updateSite}
              saveSite={saveSite}
              currentPage={currentPage}
              updateCurrentPage={updateCurrentPage}
              addChild={addChild}
              removeWidget={removeWidget}
              updateWidgetPosition={props.updateWidgetPosition}
              closeConfigPanel={showSidebar}
              eventList={props.eventList}
              fetchEventList={props.fetchEventList}
              saveEvent={props.saveEvent}
              removeEvent={props.removeEvent}
              updateEvent={props.updateEvent}
              isEditingTemplate={isEditingTemplate}
              fetchFeatures={fetchFeatures}
              features={features}
            />
          </Scrollbars>
        </SidebarWrap>
      </SidebarNav>
    </Draggable>
  ) : null;
};

export default WidgetControlWrapperNew;
